import React, { useContext } from 'react';
import StatusIndicator from '../../components/StatusIndicator';
import { COMPLETED_STATUS, IN_PROGRESS_STATUS, WIN_THRESHOLD_INFO } from '../../utils/Constants';
import { mapAppletToWidget } from '../../utils/ABHelper';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import Card from 'Components/DesignSystem/Card/Card';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ResultTable from './ResultTable';
import TrafficAllocation from './TrafficAllocation';
import { ABContext } from '../../contexts/ABContext';
import ABToast from './ABToast';

/**
 * The results card — applet name, recommendation, MLP, traffic allocation and the model
 * table (`okf/design-system/e2e.md` § 157).
 *
 * > **A ternary whose branches were identical.** The in-progress banner read
 * > `getDaysBetween(...) <= 0 ? <ABToast msg2={notification}/> : <ABToast msg2={notification}/>`
 * > — the same element either way, so the day calculation drove nothing. Collapsed to
 * > one condition. Nothing rendered differently before or after; what goes is a branch
 * > that reads as if the wording changes when a test runs past its end date, and does
 * > not.
 */
const ResultSection1 = () => {
    const abContext = useContext(ABContext);
    const { testData } = abContext;

    return (
        <section>
            {/* Same banner for IN_PROGRESS and COMPLETED — the two branches only differ
                in which status gates them. */}
            {((testData?.status === IN_PROGRESS_STATUS || testData?.status === COMPLETED_STATUS)
                && testData?.notification)
                ? <ABToast msg2={testData?.notification} />
                : null}

            <Card elevation="none" className="tw-mt-6 tw-p-2" data-testid="ab-result-card">
                <div className='tw-flex tw-items-center tw-justify-between'>
                    <div className='tw-flex tw-items-center tw-gap-4'>
                        <Typography
                            variant="title-header"
                            as="h2"
                            className="tw-text-ink"
                            data-testid="ab-result-applet-name"
                        >
                            {mapAppletToWidget(testData?.applets[0]?.applet_name ?? '')}
                        </Typography>
                        {testData?.test_recommendation
                            ? <StatusIndicator message={testData?.test_recommendation} data-testid="ab-result-recommendation" />
                            : null}
                    </div>
                    <div className='tw-flex tw-items-center tw-gap-1'>
                        <Typography variant="sub-title-label" as="p" className="tw-text-ink-tertiary" data-testid="ab-result-mlp">
                            {'MLP'}:
                            <span className='tw-text-ink tw-ms-1'>{`${testData?.win_threshold}% ${testData?.metric}`}</span>
                        </Typography>
                        <InfoTooltip message={WIN_THRESHOLD_INFO} label="About Minimum Lift Percentage" />
                    </div>
                </div>

                {/* Traffic Allocation — shows assigned vs realized per model */}
                <TrafficAllocation />

                <div className='tw-mt-4 tw-pb-4'>
                    <ResultTable />
                </div>
            </Card>
        </section>
    );
}

export default ResultSection1;
